import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowDropDownSharp,
  Info,
} from "@mui/icons-material";
import { styled } from "@mui/system";

import { AppConfig } from "../../context/context";
import { APP_LANGUAGES, USER_ROLES } from "../../utils/constants";
import { useUserService } from "../../services/user-service";
import { useService } from "../../pages/Organizations/organizations-service";
import { IOrganizationModel } from "../../models/organizations.model";
import pubSub, { EVENTS } from "../../config/pubSub";

const TopNav = styled("div")({
  ".topNav": {
    height: "100%",
    backgroundColor: "transparent",
  },
  ".toolbar": {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": {
      color: "var(--kyocera-dark-grey)",
    },
  },
  ".languageButton": {
    width: 110,
    margin: 10,
    color: "#6d6e71",
    textTransform: "none",
  },
});

const TopNavComponent = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { config, userProfile } = useContext(AppConfig) || {};

  if (!config || !userProfile) {
    throw new Error("AppConfig context is not properly initialized.");
  }
  const { updateUserLocale } = useContext(AppConfig)!;

  const { orgService } = useService();
  const { userService } = useUserService();

  const [userInitials, setUserInitials] = useState("");
  const [userLanguage, setUserLanguage] = useState("en");
  const [orgDisplayName, setOrgDisplayName] = useState("");

  const [profileMenuEl, setProfileMenuEl] = useState<null | HTMLElement>(null);
  const [languageMenuEl, setLanguageMenuEl] = useState<null | HTMLElement>(null);

  const initOrgDisplayName = useCallback(async () => {
    if (!orgService?.getOrg) return;
    try {
      const org: IOrganizationModel = await orgService?.getOrg(config.tenantId);
      setOrgDisplayName(org.displayName);
    } catch {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  }, [orgService, config.tenantId]);

  /*const fetchGuideLinks = useCallback(async () => {
    try {
      const data = await getGuideLinks();
      setGuideLinks(data.links);
    } catch (error) {
      console.log(error);
    }
  }, [getGuideLinks]);*/

  useEffect(() => {
    initOrgDisplayName();
    if (config.language) setUserLanguage(config.language);
  }, [initOrgDisplayName, config.language]);

  useEffect(() => {
    const initials =
      userProfile.firstname?.[0]?.toUpperCase() +
      userProfile.lastname?.[0]?.toUpperCase();
    setUserInitials(initials ?? "");
  }, [userProfile, config.language]);

  /*useEffect(() => {
    fetchGuideLinks();
  }, [fetchGuideLinks]);*/

  useEffect(() => {
    pubSub.subscribe(EVENTS.DISPLAY_NAME_CHANGED, (name: string) => {
      setOrgDisplayName(name);
    });
  }, []);

  const handleUpdateUserLanguage = async (locale: string) => {
    const userSetting = {
      ...userProfile,
      locale,
    };
    try {
      await userService?.updateUserSetting(userProfile.id, userSetting);
      setUserLanguage(locale);
      updateUserLocale(locale);
    } catch {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    } finally {
      setLanguageMenuEl(null);
    }
  };

  const handleProfileClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setProfileMenuEl(e.currentTarget);

  const handleLanguageClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setLanguageMenuEl(e.currentTarget);

  const handleProfileClose = () => setProfileMenuEl(null);
  const handleLanguageClose = () => setLanguageMenuEl(null);

  const handleLogOut = () => {
    setProfileMenuEl(null);
    setLanguageMenuEl(null);
    navigate("/logout", { state: { from: window.location.href } });
  };

  const handleSettings = () => {
    setProfileMenuEl(null);
    navigate("/settings");
  };

  const textRef = useRef<HTMLHeadingElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [orgDisplayName]);
  
  return (
    <TopNav>
      <AppBar position="static" className="topNav">
        <Toolbar
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        >
          <div style={{ minWidth: 0, flex: 1, width: '0' }}>
            <Tooltip title={orgDisplayName} disableHoverListener={!isOverflowing}>
              <Typography
              component={"h3"}
              ref={textRef}
              style={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                paddingRight: "20px",
              }}
              sx={{
                color: "var(--kyocera-dark-grey)",
                fontSize: "24px",
              }}
              >
                {orgDisplayName}
              </Typography>
            </Tooltip>
          </div>
          <div style={{ flexShrink: 0 }}>

            {/*
            <Button onClick={handleLanguageClick} className="languageButton">
              {APP_LANGUAGES[userProfile.locale || userLanguage].full}
              <ArrowDropDownSharp />
            </Button>
            */}

            <IconButton
              edge="start"
              aria-label="profile"
              onClick={handleProfileClick}
              sx={{
                color: "var(--kyocera-white) !important",
                padding: "5px !important",
                width: "40px",
                height: "40px",
                borderRadius: "50% !important",
                background: "var(--kyocera-blue) !important",
                "&:hover": {
                  backgroundColor: "var(--kyocera-blue) !important",
                },
              }}
            >
              {userInitials}
            </IconButton>

            <Menu
              id="profile-menu"
              anchorEl={profileMenuEl}
              open={Boolean(profileMenuEl)}
              onClose={handleProfileClose}
              sx={{
                "& .MuiMenu-list": {
                  display: "flex",
                  flexDirection: "column",
                },
                "& .MuiButtonBase-root": {
                  padding: "6px 16px",
                  justifyContent: "flex-start",
                  display: "block",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  padding: "6px 16px",
                  textTransform: "capitalize",
                }}
              >
                {`${userProfile?.firstname} ${userProfile?.lastname}`}
              </div>

              {USER_ROLES.admins.includes(userProfile.role) && (
                <MenuItem onClick={handleSettings} className="menuItem" dense>
                  {t("menu_option_settings")}
                </MenuItem>
              )}
              <MenuItem onClick={handleLogOut} className="menuItem" dense>
                {t("menu_option_logout")}
              </MenuItem>
            </Menu>

            {/*
            <Menu
              id="language-menu"
              anchorEl={languageMenuEl}
              open={Boolean(languageMenuEl)}
              onClose={handleLanguageClose}
              className="customMenuStyles"
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              {Object.entries(APP_LANGUAGES).map(([_, { code, full }]) => (
                <MenuItem
                  key={code}
                  onClick={() => handleUpdateUserLanguage(code)}
                  className="menuItem"
                  dense
                >
                  {full}
                </MenuItem>
              ))}
            </Menu>
            */}
          </div>
        </Toolbar>
      </AppBar>
    </TopNav>
  );
};

export default TopNavComponent;
